import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

const RESPONSE_LABEL_KEYS: Record<string, string> = {
	ACCEPTED: "inbox.calendarResponse.accepted",
	TENTATIVE: "inbox.calendarResponse.tentative",
	DECLINED: "inbox.calendarResponse.declined",
	RESCHEDULED: "inbox.calendarResponse.rescheduled",
};

/** "Accepted: Kick Off" → "Kick Off": the calendar client's localized prefix is what the line already says. */
const stripResponsePrefix = (subject: string | null | undefined): string =>
	(subject ?? "").replace(/^[^:]{1,40}:\s*/, "").trim();

/**
 * A lead's calendar client answering our invitation (PREC-3901). The mail behind it carries no
 * message the lead wrote — an empty body, a signature, or a client notification — so the thread
 * shows one line saying what happened to the meeting instead of a bubble and a sentiment.
 */
export const CalendarResponseLine: FunctionComponent<{ conversation: Conversation }> = ({
	conversation,
}) => {
	const { t } = useTranslation();
	const labelKey = RESPONSE_LABEL_KEYS[conversation.calendarResponse ?? ""];
	const meeting = stripResponsePrefix(conversation.subject);

	if (!labelKey) {
		return null;
	}

	return (
		<div
			className="text-xs text-gray-700 font-normal flex items-center gap-1"
			data-testid="calendar-response-line"
		>
			<Icon icon={faCalendarCheck} />
			<span>{t(labelKey)}</span>
			{meeting && (
				<>
					<span aria-hidden="true">·</span>
					<span className="font-semibold text-text">{meeting}</span>
				</>
			)}
		</div>
	);
};
